/* === file: /frontend/src/pages/GameAdminCore.jsx === */
import React, { useEffect, useMemo, useState } from 'react';
import { socket } from '../socket';

import ic1800ad from '../assets/1800ad.png';
import icff66c4 from '../assets/ff66c4.png';
import ic38b6ff from '../assets/38b6ff.png';
import ic5bce21 from '../assets/5bce21.png';
import icf6c700 from '../assets/f6c700.png';
import ice62129 from '../assets/e62129.png';

const ICONS = { '1800ad': ic1800ad, 'ff66c4': icff66c4, '38b6ff': ic38b6ff, '5bce21': ic5bce21, 'f6c700': icf6c700, 'e62129': ice62129 };
const hexToIcon = (hex='') => ICONS[String(hex).replace('#','').toLowerCase()] || '';

const API = process.env.REACT_APP_API_BASE || '';
const ORDER = ['#1800ad','#ff66c4','#38b6ff','#5bce21','#f6c700','#e62129'].map(s=>s.toLowerCase());
const LINES = [
  [0,1,2,3], [4,5,6,7], [8,9,10,11], [12,13,14,15],
  [0,4,8,12],[1,5,9,13],[2,6,10,14],[3,7,11,15],
  [0,5,10,15],[3,6,9,12],
];

function describePendingLine(lineCode) {
  const cells = String(lineCode || '')
    .split('-')
    .map(v => Number(v))
    .filter(v => Number.isFinite(v));
  if (cells.length < 2) return { dir: '', cellsLabel: '' };
  const a = cells[0];
  const d = cells[cells.length - 1];
  const ra = Math.floor(a / 4), ca = a % 4;
  const rd = Math.floor(d / 4), cd = d % 4;
  const dr = rd - ra, dc = cd - ca;
  let dir = '連線';
  if (dr === 0) dir = '橫列';
  else if (dc === 0) dir = '直行';
  else if (dr === dc) dir = '斜線 ╲';
  else if (dr === -dc) dir = '斜線 ╱';
  const cellsLabel = cells.map((i) => String(Number(i) + 1)).join('-');
  return { dir, cellsLabel };
}

function orderCellBadges(teamsArr, lockTeamIds) {
  const raw = (Array.isArray(teamsArr) ? teamsArr : []).slice(0, 2);
  const locks = Array.isArray(lockTeamIds) ? lockTeamIds : [];
  const lockSet = new Set(locks.map((id) => Number(id)));
  const hasLock = (tid) => lockSet.has(Number(tid));
  const locked = raw.filter((tid) => hasLock(tid));
  const unlocked = raw.filter((tid) => !hasLock(tid));
  return [...locked, ...unlocked].slice(0, 2);
}

/**
 * props:
 * - allowedHex: string[]  例如 ['1800ad','ff66c4'] 或 ['#1800ad','#ff66c4']
 * - allowedIds: number[]  例如 [1, 2]
 * - title:     string     例如 "連線系統後台 A"
 *
 * URL 覆蓋：
 * - ?hex=1800ad,ff66c4
 * - ?ids=1,2
 * - helperMode: 小幫手專用（隱藏開局／重置時鐘等；白名單僅能操作 allowedIds）
 */
export default function GameAdminCore({ allowedHex = [], allowedIds = [], title = '連線系統後台', helperMode = false }){
  const [state, setState]     = useState({ occupancy:Array.from({ length: 16 }, () => []), cellLocks:Array.from({ length: 16 }, () => []), teams:[], claims:[], scores:{}, scoredLines:[], lastVictory:null });
  const [teamId, setTeamId]   = useState(null);
  const [cell, setCell]       = useState(null);
  const [plays, setPlays]     = useState([]);
  const [busy, setBusy]       = useState(false);
  const [msg, setMsg]         = useState('');
  const [openPlays, setOpenPlays] = useState(false);
  const [globalTimer, setGlobalTimer] = useState({ running:false, paused:false, startAt:null, endsAt:null, remainingMs:0, participatingTeamIds:null });
  const [tick, setTick]       = useState(0);
  const [selectedCell, setSelectedCell] = useState(null);
  const [showTeamSelector, setShowTeamSelector] = useState(false);
  const [claimDecisionSecStr, setClaimDecisionSecStr] = useState('0.5');

  // 解析網址參數（可覆蓋 props）
  const param = useMemo(() => {
    const u = new URL(window.location.href);
    const hexStr = u.searchParams.get('hex');
    const idsStr = u.searchParams.get('ids');
    const hx = (hexStr ? hexStr.split(',') : allowedHex).map(s => s?.replace('#','').toLowerCase()).filter(Boolean);
    const ids = (idsStr ? idsStr.split(',') : allowedIds).map(v => Number(v)).filter(v => Number.isFinite(v));
    return { hx, ids };
  }, [allowedHex, allowedIds, helperMode]);

  useEffect(() => {
    const onState    = (s) => setState({ ...s, cellLocks: s.cellLocks || Array.from({ length: 16 }, () => []) });
    const onPlaysNew = () => reloadPlays();
    const onReset    = () => reloadAll();
    const onTimer    = (t) => setGlobalTimer(t);
    socket.on('game:state', onState);
    socket.on('plays:new', onPlaysNew);
    socket.on('game:reset', onReset);
    socket.on('game:timer', onTimer);
    return () => {
      socket.off('game:state', onState);
      socket.off('plays:new', onPlaysNew);
      socket.off('game:reset', onReset);
      socket.off('game:timer', onTimer);
    };
  }, []);

  useEffect(() => { reloadAll(); }, []);
  useEffect(() => { (async () => { try { const r = await fetch(`${API}/api/game/timer`); if (r.ok) setGlobalTimer(await r.json()); } catch {} })(); }, []);
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/game/settings`);
        if (!r.ok) return;
        const j = await r.json();
        if (Number.isFinite(j.claimDecisionSeconds)) setClaimDecisionSecStr(String(j.claimDecisionSeconds));
      } catch { /* 略過 */ }
    })();
  }, []);
  useEffect(() => { const t = setInterval(()=>setTick(x=>x+1), 1000); return () => clearInterval(t); }, []);

  const reloadAll = async () => {
    try {
      const [s, p] = await Promise.all([
        fetch(`${API}/api/game/state`).then(r => r.json()),
        fetch(`${API}/api/game/plays?top=200`).then(r => r.json()),
      ]);
      setState({ ...s, cellLocks: s.cellLocks || Array.from({ length: 16 }, () => []) }); setPlays(p.plays || []);
    } catch {}
  };
  const reloadPlays = async () => { try { const r = await fetch(`${API}/api/game/plays?top=200`); const j = await r.json(); setPlays(j.plays || []); } catch {} };

  // 允許清單（同時支援 ID 與 HEX）
  const allowedSet = useMemo(() => {
    const byId  = new Set(param.ids);
    const byHex = new Set(param.hx.map(h => h.replace('#','').toLowerCase()));
    return { byId, byHex };
  }, [param]);

  const teamsSortedAll = useMemo(() => {
    const arr = (state.teams || []).map(t => ({ ...t, __icon: hexToIcon(t.COLOR_HEX) }));
    return arr.sort((a,b)=>ORDER.indexOf(String(a.COLOR_HEX||'').toLowerCase())-ORDER.indexOf(String(b.COLOR_HEX||'').toLowerCase()));
  }, [state.teams]);

  // 依白名單顯示可選隊伍（未給條件則全部隊伍，方便輪替登記）
  const teamsAllowed = useMemo(() => {
    return teamsSortedAll.filter(t => {
      const idOK = (helperMode || allowedSet.byId.size)
        ? allowedSet.byId.has(Number(t.TEAM_ID))
        : true;
      const hexOK = allowedSet.byHex.size ? allowedSet.byHex.has(String(t.COLOR_HEX || '').replace('#','').toLowerCase()) : true;
      return idOK && hexOK;
    });
  }, [teamsSortedAll, allowedSet, helperMode]);

  // 送出限制：只能送允許的隊伍
  const teamIdAllowed = useMemo(() => {
    if (teamId == null) return false;
    const t = (state.teams || []).find(x => x.TEAM_ID === teamId);
    if (!t) return false;
    const idOK = (helperMode || allowedSet.byId.size)
      ? allowedSet.byId.has(Number(teamId))
      : true;
    const hexOK = allowedSet.byHex.size ? allowedSet.byHex.has(String(t.COLOR_HEX || '').replace('#','').toLowerCase()) : true;
    return idOK && hexOK;
  }, [teamId, state.teams, allowedSet, helperMode]);

  const occOnCell = useMemo(() => {
    if (cell == null) return [];
    return Array.isArray(state.occupancy?.[cell]) ? state.occupancy[cell] : [];
  }, [cell, state.occupancy]);

  const alreadyInCell = useMemo(() => {
    if (teamId == null || cell == null) return false;
    return occOnCell.includes(teamId);
  }, [teamId, cell, occOnCell]);

  const bothLockedOnCell = useMemo(() => {
    if (cell == null || occOnCell.length < 2) return false;
    const locks = state.cellLocks?.[cell] || [];
    const ls = new Set(locks.map(Number));
    return occOnCell.every((tid) => ls.has(Number(tid)));
  }, [cell, occOnCell, state.cellLocks]);

  const teamsForChips = useMemo(() => {
    if (bothLockedOnCell) return [];
    return teamsAllowed.filter((t) => !occOnCell.includes(t.TEAM_ID));
  }, [teamsAllowed, occOnCell, bothLockedOnCell]);

  useEffect(() => {
    if (bothLockedOnCell) setTeamId(null);
  }, [bothLockedOnCell]);

  useEffect(() => {
    if (teamId != null && occOnCell.includes(teamId)) setTeamId(null);
  }, [occOnCell, teamId]);

  const pendingClaims = useMemo(() => {
    const now = Date.now();
    const pend = (state.claims || []).filter(c => !c.status || c.status === 'pending');
    const list = pend.map(c => {
      const left = c.expiresAt ? Math.max(0, c.expiresAt - now) : Math.max(0, c.remainingMs || 0);
      return { ...c, left };
    });
    return list.sort((a,b)=> (a.left - b.left)).slice(0, 3);
  }, [state.claims, tick]);

  const submit = async () => {
    setMsg('');
    if (teamId == null || cell == null) return;
    if (!teamIdAllowed) { setMsg('此後台不允許操作該隊伍'); return; }
    if (alreadyInCell)  { setMsg('此隊伍已在該格，請改選其他隊伍'); return; }
    if (bothLockedOnCell) { setMsg('該格兩隊皆已鎖定，無法再登記'); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/game/play`, {
        method:'POST', headers:{ 'Content-Type':'application/json' },
        body: JSON.stringify({ teamId, cell })
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        setMsg(j.message || j.error || '送出失敗');
        return;
      }
      setCell(null);
      setTeamId(null);
      await reloadAll();
    } finally { setBusy(false); }
  };

  const remove = async (id) => { if (!window.confirm('確定刪除此明細？')) return; await fetch(`${API}/api/game/plays/${id}`, { method:'DELETE' }); await reloadAll(); };
  const reset  = async (mode='full') => {
    if (!window.confirm(mode === 'full' ? '重置全部（含分數與亮線）？' : '重置盤面（保留分數與得分線）？')) return;
    setBusy(true);
    try {
      await fetch(`${API}/api/game/reset`, { method:'POST', headers:{ 'Content-Type':'application/json' }, body: JSON.stringify({ mode }) });
      setTeamId(null); setCell(null);
      await reloadAll();
    } finally { setBusy(false); }
  };

  const timerAction = async (action) => {
    const r = await fetch(`${API}/api/game/timer`, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ action }) });
    const t = await r.json(); setGlobalTimer(t);
  };

  const pendingKey = useMemo(() => new Set(pendingClaims.map(c => `${c.lineCode}|${c.teamId}`)), [pendingClaims]);

  const findTeamName = (id) => (state.teams||[]).find(t => t.TEAM_ID===id)?.TEAM_NAME;

  const isClockCounting = globalTimer.running && !globalTimer.paused && (globalTimer.remainingMs || 0) > 0;
  const mayPlacePieces = helperMode ? isClockCounting : true;

  const selectorOcc = useMemo(() => {
    if (selectedCell == null) return [];
    return Array.isArray(state.occupancy?.[selectedCell]) ? state.occupancy[selectedCell] : [];
  }, [selectedCell, state.occupancy]);

  const selectorBothLocked = useMemo(() => {
    if (selectedCell == null || selectorOcc.length < 2) return false;
    const locks = state.cellLocks?.[selectedCell] || [];
    const ls = new Set(locks.map(Number));
    return selectorOcc.every((tid) => ls.has(Number(tid)));
  }, [selectedCell, selectorOcc, state.cellLocks]);

  const teamsSelectable = useMemo(() => {
    if (selectorBothLocked) return [];
    return teamsAllowed.filter((t) => !selectorOcc.includes(t.TEAM_ID));
  }, [teamsAllowed, selectorOcc, selectorBothLocked]);

  const participatingSet = useMemo(() => {
    const pids = globalTimer.participatingTeamIds;
    if (!pids || !pids.length) return null;
    return new Set(pids.map(Number));
  }, [globalTimer.participatingTeamIds]);

  const isTeamParticipating = (tid) => {
    if (!participatingSet) return true;
    return participatingSet.has(Number(tid));
  };

  const teamScores = useMemo(() => {
    const scores = state.scores || {};
    return teamsSortedAll.map((team) => ({
      ...team,
      score: scores[team.TEAM_ID] || 0,
    })).sort((a, b) => b.score - a.score);
  }, [teamsSortedAll, state.scores]);

  const showGameEndedBanner = useMemo(() => {
    const t = globalTimer;
    if (t.paused) return false;
    return !t.running || (t.remainingMs || 0) <= 0;
  }, [globalTimer]);

  const matchStatusLabel = useMemo(() => {
    if (globalTimer.paused) return '已暫停';
    if (isClockCounting) return '進行中';
    if (globalTimer.running) return '進行中';
    if ((globalTimer.startAt || globalTimer.endsAt) && showGameEndedBanner) return '已結束';
    return '未開始';
  }, [globalTimer, isClockCounting, showGameEndedBanner]);

  const matchParticipatingTeams = useMemo(() => {
    const pids = globalTimer.participatingTeamIds;
    if (pids && pids.length) {
      return teamsSortedAll.filter((t) => pids.includes(t.TEAM_ID));
    }
    if (globalTimer.running || globalTimer.startAt) return teamsSortedAll;
    return [];
  }, [globalTimer, teamsSortedAll]);

  const matchDurationLabel = useMemo(() => {
    if (globalTimer.startAt && globalTimer.endsAt) {
      const mins = Math.round((globalTimer.endsAt - globalTimer.startAt) / 60000);
      return `${mins} 分鐘`;
    }
    return '—';
  }, [globalTimer]);

  const handleCellClick = (cellIndex) => {
    if (!mayPlacePieces) return;
    setSelectedCell(cellIndex);
    setShowTeamSelector(true);
  };

  const handleTeamSelect = async (pickTeamId) => {
    if (selectedCell == null || pickTeamId == null) return;
    const t = teamsSortedAll.find((x) => x.TEAM_ID === pickTeamId);
    const idOK = (helperMode || allowedSet.byId.size)
      ? allowedSet.byId.has(Number(pickTeamId))
      : true;
    const hexOK = allowedSet.byHex.size
      ? allowedSet.byHex.has(String(t?.COLOR_HEX || '').replace('#', '').toLowerCase())
      : true;
    if (!t || !idOK || !hexOK) {
      setMsg('此後台不允許操作該隊伍');
      setShowTeamSelector(false);
      setSelectedCell(null);
      return;
    }
    if (!isTeamParticipating(pickTeamId)) {
      setMsg('該隊伍未參加本局');
      return;
    }
    if (selectorOcc.includes(pickTeamId)) {
      setMsg('此隊伍已在該格');
      return;
    }
    if (selectorBothLocked) {
      setMsg('該格兩隊皆已鎖定，無法再登記');
      return;
    }
    setBusy(true);
    setMsg('');
    try {
      const r = await fetch(`${API}/api/game/play`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ teamId: pickTeamId, cell: selectedCell }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        setMsg(j.message || j.error || '送出失敗');
        setShowTeamSelector(false);
        setSelectedCell(null);
        return;
      }
      setShowTeamSelector(false);
      setSelectedCell(null);
      await reloadAll();
    } catch {
      setMsg('送出失敗，請重試');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin-m-wrap">
      <style>{css}</style>

      <header className="topbar">
        <div className="title">{title}</div>
        {!helperMode && (
          <div className="actions">
            <button className="ghost" onClick={() => timerAction('start')} disabled={busy}>開始</button>
            <button className="ghost" onClick={() => timerAction('stop')}  disabled={busy}>停止</button>
            <button className="danger" onClick={() => timerAction('reset')} disabled={busy}>重設時鐘</button>
          </div>
        )}
      </header>

      {state.lastVictory && (
        <div className="admin-line-score-strip" role="status">
          <span className="admin-line-score-text">
            {(state.teams||[]).find(t=>t.TEAM_ID===state.lastVictory.teamId)?.TEAM_NAME || '隊伍'}
            連線鎖格得分
            {(() => {
              const { cellsLabel } = describePendingLine(state.lastVictory.lineCode);
              return cellsLabel ? `（格 ${cellsLabel}）` : '';
            })()}
          </span>
        </div>
      )}

      <div className="clock-wrap">
        <BigClock timer={globalTimer} />
        {helperMode && showGameEndedBanner && (
          <div className="game-ended-banner">
            <span className="game-ended-text">遊戲已結束</span>
          </div>
        )}
        {pendingClaims.length > 0 && (
          <div className="countdown-stack">
            {pendingClaims.map(c => (
              <MiniCountdown key={`${c.lineCode}|${c.teamId}`} claim={c} team={(state.teams||[]).find(t=>t.TEAM_ID===c.teamId)} />
            ))}
          </div>
        )}
        {helperMode && (
          <section className="match-panel">
            <div className="match-panel-head">
              <span className="match-panel-title">本局比賽</span>
              <span className={`match-status ${matchStatusLabel === '進行中' ? 'on' : ''}`}>{matchStatusLabel}</span>
            </div>
            <div className="match-meta">
              <span>時長 {matchDurationLabel}</span>
              <span>連線判定 {claimDecisionSecStr} 秒</span>
            </div>
            <div className="match-teams">
              {matchParticipatingTeams.length ? (
                matchParticipatingTeams.map((t) => (
                  <span key={t.TEAM_ID} className="match-team-chip" title={t.TEAM_NAME}>
                    <span className="match-dot" style={{ background: t.COLOR_HEX || '#444' }} />
                    {t.TEAM_NAME}
                  </span>
                ))
              ) : (
                <span className="muted">尚未開始</span>
              )}
            </div>
          </section>
        )}
      </div>

      {!helperMode && (
      <div className="toolbar-reset">
        <button onClick={() => reset('soft')} disabled={busy}>重置(保留分數)</button>
        <button onClick={() => reset('full')} disabled={busy} className="danger">重置(全部清空)</button>
      </div>
      )}

      <main className="content">
        {helperMode && (
          <section className="panel">
            <h3 className="panel-title">分數（佔格 +1；連線鎖定另加獎勵分）</h3>
            <div className="score-board score-board-row">
              {teamScores.map((team, idx) => (
                <div
                  key={team.TEAM_ID}
                  className={`score-chip-inline ${idx === 0 && team.score > 0 ? 'top-score' : ''}`}
                  title={team.TEAM_NAME}
                >
                  <span className="sc-rank">#{idx + 1}</span>
                  <span className="sc-dot" style={{ background: team.COLOR_HEX || '#444' }} />
                  <span className="sc-name">{team.TEAM_NAME}</span>
                  <span className="sc-val">{team.score}</span>
                </div>
              ))}
              {teamScores.length === 0 && (
                <div className="score-empty">尚無分數</div>
              )}
            </div>
          </section>
        )}

        <section className="panel">
          <h3 className="panel-title">{helperMode ? '盤面預覽（點擊格子選擇隊伍）' : '盤面預覽'}</h3>
          <div className="board">
            <Grid
              occupancy={state.occupancy}
              cellLocks={state.cellLocks && state.cellLocks.length === 16 ? state.cellLocks : Array.from({ length: 16 }, () => [])}
              teams={teamsSortedAll}
              pendingKey={pendingKey}
              onCellClick={helperMode ? handleCellClick : undefined}
              selectedCell={helperMode ? selectedCell : null}
              disabled={helperMode ? (!mayPlacePieces || busy) : false}
            />
          </div>
        </section>

        {!helperMode && (
          <>
        <section className="panel">
          <h3 className="panel-title">選擇隊伍（已佔領所選格之隊伍不會列出）</h3>
          {bothLockedOnCell && cell != null && (
            <div className="warn">此格兩隊皆已鎖定（連線得分），無法再登記第三隊。</div>
          )}
          <div className="chips" role="tablist" aria-label="teams">
            {teamsForChips.map(t => (
              <button key={t.TEAM_ID}
                className={`chip ${teamId === t.TEAM_ID ? 'active' : ''}`}
                style={{ '--clr': t.COLOR_HEX || '#444' }}
                onClick={() => setTeamId(t.TEAM_ID)} disabled={busy} title={t.TEAM_NAME}>
                <span className="dot" style={{ background: t.COLOR_HEX || '#444' }} />
                <span className="chip-text">{t.TEAM_NAME}</span>
              </button>
            ))}
          </div>
          {(allowedSet.byId.size || allowedSet.byHex.size) && teamsAllowed.length === 0 && (
            <div className="warn">（提示）允許清單內沒有隊伍，請確認參數或資料。</div>
          )}
        </section>

        <section className="panel">
          <h3 className="panel-title">選擇格子</h3>
          <div className="keypad">
            {Array.from({ length: 16 }).map((_, i) => (
              <button key={i} className={`num ${cell === i ? 'active' : ''}`} onClick={() => setCell(i)} disabled={busy}>{i + 1}</button>
            ))}
          </div>
        </section>
          </>
        )}

        {helperMode && (allowedSet.byId.size || allowedSet.byHex.size) && teamsAllowed.length === 0 && (
          <div className="warn">（提示）允許清單內沒有隊伍，請確認參數或資料。</div>
        )}

        {(msg) && <div className="warn">{msg}</div>}

        <section className="panel">
          <button className="collapse" onClick={() => setOpenPlays(v => !v)} aria-expanded={openPlays}>
            明細（可刪除誤傳）{openPlays ? '▲' : '▼'}
          </button>
          {openPlays && (
            <div className="table-wrap">
              <table>
                <thead><tr><th>ID</th><th>時間(UTC)</th><th>隊伍</th><th>格</th><th></th></tr></thead>
                <tbody>
                  {plays.map(p => (
                    <tr key={p.PLAY_ID}>
                      <td>{p.PLAY_ID}</td>
                      <td>{new Date(p.CREATED_AT).toISOString().slice(0,19).replace('T',' ')}</td>
                      <td>{p.TEAM_NAME}</td>
                      <td>{p.CELL + 1}</td>
                      <td><button className="link danger" onClick={() => remove(p.PLAY_ID)} disabled={busy}>刪除</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <div style={{ height: helperMode ? '24px' : '80px' }} />
      </main>

      {helperMode && showTeamSelector && (
        <div className="team-selector-overlay" onClick={() => { setShowTeamSelector(false); setSelectedCell(null); }}>
          <div className="team-selector-panel" onClick={(e) => e.stopPropagation()}>
            <div className="team-selector-title">選擇隊伍（格子 {selectedCell != null ? selectedCell + 1 : ''}）</div>
            {selectorBothLocked && (
              <div className="team-selector-hint">此格兩隊皆已鎖定（連線得分），無法再登記第三隊。</div>
            )}
            {!selectorBothLocked && (
              <div className="team-selector-hint">未參加本局者灰顯不可選；已佔領此格者不可選；格子已滿時會擠位或拒絕。</div>
            )}
            <div className="team-selector-chips">
              {teamsAllowed.map((t) => {
                const inCell = selectorOcc.includes(t.TEAM_ID);
                const notPart = !isTeamParticipating(t.TEAM_ID);
                const chipDisabled = busy || selectorBothLocked || inCell || notPart;
                return (
                  <button
                    key={t.TEAM_ID}
                    type="button"
                    className={`team-selector-chip${notPart ? ' disabled-part' : ''}`}
                    style={{ '--clr': t.COLOR_HEX || '#444' }}
                    onClick={() => !chipDisabled && handleTeamSelect(t.TEAM_ID)}
                    disabled={chipDisabled}
                    title={notPart ? `${t.TEAM_NAME}（未參加本局）` : t.TEAM_NAME}
                  >
                    <span className="dot" style={{ background: t.COLOR_HEX || '#444' }} />
                    <span className="chip-text">{t.TEAM_NAME}</span>
                  </button>
                );
              })}
            </div>
            <button type="button" className="team-selector-cancel" onClick={() => { setShowTeamSelector(false); setSelectedCell(null); }}>取消</button>
          </div>
        </div>
      )}

      {!helperMode && (
      <footer className="submitbar">
        <div className="summary">
          <span>隊伍：<b>{findTeamName(teamId) || '—'}</b></span>
          <span>格子：<b>{cell!=null ? (cell+1) : '—'}</b></span>
        </div>
        <button className="submit" disabled={busy || teamId == null || cell == null || alreadyInCell || bothLockedOnCell || !teamIdAllowed} onClick={submit}>送出</button>
      </footer>
      )}
    </div>
  );
}

function BigClock({ timer }){
  const paused = timer?.paused;
  const sec = Math.max(0, Math.ceil((timer?.remainingMs || 0) / 1000));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  const text = h > 0 ? `${h}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}` : `${m}:${String(s).padStart(2,'0')}`;
  return (
    <div className="bigclock">
      {paused && <span className="paused-pill">已暫停</span>}
      <span className="clock">{text}</span>
    </div>
  );
}
function MiniCountdown({ claim, team }){
  const sec = Math.ceil((claim.left || 0) / 1000);
  const { dir, cellsLabel } = describePendingLine(claim.lineCode);
  return (
    <div className="mini-countdown">
      <span className="dot" style={{ background: team?.COLOR_HEX }} />
      <span className="mini-meta">
        <b>{team?.TEAM_NAME}</b>
        <span className="mini-lineinfo">{dir}{cellsLabel ? ` · 格${cellsLabel}` : ''}</span>
      </span>
      <span className="time">{Math.floor(sec/60)}:{String(sec%60).padStart(2,'0')}</span>
    </div>
  );
}

function Grid({ occupancy = [], cellLocks = [], teams = [], pendingKey = new Set(), onCellClick, selectedCell, disabled = false }){
  const map = {};
  for (const t of teams) { map[t.TEAM_ID] = t; map[String(t.TEAM_ID)] = t; }
  const N = 4, cellPct = 100 / N;
  return (
    <div className="grid small">
      {Array.from({ length: 16 }).map((_, i) => (
        (() => {
          const display = orderCellBadges(occupancy[i], cellLocks[i]);
          const lockIds = Array.isArray(cellLocks[i]) ? [...new Set(cellLocks[i])] : [];
          const lockSet = new Set(lockIds.map((id) => Number(id)));
          const hasLock = (x) => lockSet.has(Number(x));
          const bothLocked = display.length === 2 && display.every((tid) => hasLock(tid));
          const clickDisabled = disabled || bothLocked;
          return (
        <div key={i} className={`cell ${bothLocked ? 'cell-both-locked' : ''} ${selectedCell === i ? 'cell-selected' : ''} ${clickDisabled ? 'cell-disabled' : ''}`} style={{
          position:'absolute',
          left:`${(i%N)*cellPct}%`, top:`${Math.floor(i/N)*cellPct}%`,
          width:`${cellPct}%`, height:`${cellPct}%`,
          cursor: clickDisabled ? 'not-allowed' : (onCellClick ? 'pointer' : 'default'),
        }}
          onClick={() => !clickDisabled && onCellClick && onCellClick(i)}
        >
          {bothLocked ? <span className="cell-lock-ring" aria-hidden /> : null}
          <div className="cell-num">{i + 1}</div>
          <div className="badges">
            {display.map((tid, idx) => {
                const isFade = display.length === 2 && idx === 1 && !hasLock(tid);
                const t = map[tid] || map[String(tid)] || {};
                const src = t.__icon;
                const color = t.COLOR_HEX || '#bbb';
                const showLockDot = hasLock(tid) && !bothLocked;
                return (
                  <div key={`${tid}-${idx}`} className={`badge ${isFade ? 'badge-fade' : ''}`}>
                    {src ? (
                      <img
                        src={src}
                        alt=""
                        draggable={false}
                        className={showLockDot ? 'badge-image-locked' : ''}
                      />
                    ) : (
                      <div
                        className={showLockDot ? 'badge-fallback badge-fallback-locked' : ''}
                        style={{
                          width:'100%', height:'100%', borderRadius:'var(--badge-radius)',
                          background: color, border: showLockDot ? '3px solid #f01616' : '2px solid rgba(0,0,0,.08)',
                          boxSizing: 'border-box',
                        }}
                      />
                    )}
                  </div>
                );
              })}
          </div>
        </div>
          );
        })()
      ))}

      {LINES.map((line, idx) => (
        <AdminLine key={idx} line={line} occupancy={occupancy} teamsMap={map} pendingKey={pendingKey} />
      ))}
    </div>
  );
}

function AdminLine({ line, occupancy, teamsMap, pendingKey }){
  const [a,b,c,d] = line;
  const A = (occupancy[a]||[]), B = (occupancy[b]||[]), C = (occupancy[c]||[]), D = (occupancy[d]||[]);
  const candidate = (A[0] && A[0]===B[0] && A[0]===C[0] && A[0]===D[0]) ? [A[0]] : [];
  const N = 4;
  const center = i => ({ x:(i%N)+0.5, y:Math.floor(i/N)+0.5 });
  const p1 = center(a), p2 = center(d);
  const x1=(p1.x/N)*100, y1=(p1.y/N)*100, x2=(p2.x/N)*100, y2=(p2.y/N)*100;
  const dx=x2-x1, dy=y2-y1, len=Math.hypot(dx,dy), ang=Math.atan2(dy,dx)*180/Math.PI;
  const baseStyle = { left:`${x1}%`, top:`${y1}%`, width:`${len}%`, transform:`rotate(${ang}deg)` };
  const key = line.join('-');

  return (
    <>
      {candidate.map(teamId => {
        const color = teamsMap[teamId]?.COLOR_HEX || '#fff';
        const isPending = pendingKey.has(`${key}|${teamId}`);
        return (
          <div key={`pending-${teamId}`} className={`line ${isPending ? 'pending' : ''}`} style={baseStyle}>
            <div className="stroke-border" />
            <div className="stroke-color" style={{ '--clr': color }} />
          </div>
        );
      })}
    </>
  );
}

const css = `
:root { --safe-top: env(safe-area-inset-top, 0px); --safe-bottom: env(safe-area-inset-bottom, 0px); --badge-size: 65%; --badge-gap: 0%; --badge-radius: 8%;
  /* 雙鎖格外層紅框：與主後台相同，改 inset／線寬／顏色（本檔下方 const css 的 :root） */
  --cell-lock-ring-inset-top: 2%; --cell-lock-ring-inset-right: 2%; --cell-lock-ring-inset-bottom: 2%; --cell-lock-ring-inset-left: 2%;
  --cell-lock-ring-border-width: 5px; --cell-lock-ring-color: #e50914;
}
* { box-sizing: border-box; -webkit-tap-highlight-color: transparent; }
html, body, #root { height: 100%; }
body { margin: 0; font-family: system-ui, -apple-system, "PingFang TC", "Microsoft JhengHei", sans-serif; color: #1c1c1c; }
button { font-family: inherit; }
.admin-m-wrap { display: grid; grid-template-rows: auto auto auto 1fr auto; height: 100dvh; background: #faf7f4; }

.admin-line-score-strip{ display:flex; justify-content:center; padding:6px 10px; background:#222; color:#fff; border-bottom:1px solid rgba(0,0,0,.08); }
.admin-line-score-text{ font-size:13px; font-weight:900; letter-spacing:.04em; text-align:center; line-height:1.35; }

.topbar { position: sticky; top: 0; display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: calc(10px + var(--safe-top)) 12px 10px 12px; background: #fff; border-bottom: 1px solid rgba(0,0,0,.06); z-index: 10; }
.topbar .title { font-weight: 900; letter-spacing: .06em; }
.topbar .actions { display: flex; gap: 8px; }
.topbar .ghost { padding: 10px 12px; border-radius: 10px; border: 1px solid rgba(0,0,0,.12); background: #fff; font-weight: 800; }
.topbar .danger { padding: 10px 12px; border-radius: 10px; border: 1px solid #c21; background: #fef2f2; color: #c21; font-weight: 900; }

.clock-wrap{ padding: 6px 12px 0; }
.bigclock{ display:flex; flex-direction:column; align-items:center; justify-content:center; gap:4px; margin:2px 0 6px; }
.bigclock .paused-pill{ font-size:12px; font-weight:900; color:#c21; padding:2px 8px; border-radius:8px; background:#fee; border:1px solid #fcc; }
.bigclock .clock{ color:#111; font-weight:900; letter-spacing:.08em; font-size:24px; }
.countdown-stack{ display:flex; flex-direction:column; gap:6px; }
.mini-countdown{ display:flex; align-items:center; gap:8px; padding:6px 10px; background:#fff; border:1px solid #eee; border-radius:10px; font-weight:800; letter-spacing:.02em; font-size:13px; line-height:1.25; }
.mini-countdown .dot{ width:10px; height:10px; border-radius:999px; display:inline-block; flex-shrink:0; }
.mini-countdown .mini-meta{ display:flex; flex-direction:column; align-items:flex-start; gap:1px; min-width:0; flex:1; }
.mini-countdown .mini-lineinfo{ font-size:11px; font-weight:700; color:#555; word-break:break-all; }
.mini-countdown .time{ font-variant-numeric: tabular-nums; margin-left:auto; flex-shrink:0; }

.match-panel{ padding:10px 12px 12px; background:#fff; border-bottom:1px solid rgba(0,0,0,.06); }
.match-panel-head{ display:flex; align-items:center; justify-content:space-between; gap:8px; margin-bottom:8px; }
.match-panel-title{ font-weight:900; font-size:15px; }
.match-status{ font-size:12px; font-weight:800; padding:4px 10px; border-radius:999px; background:#eee; color:#555; }
.match-status.on{ background:#e8f5e9; color:#1b5e20; }
.match-meta{ display:flex; flex-wrap:wrap; gap:12px; font-size:13px; font-weight:700; color:#555; margin-bottom:8px; }
.match-teams{ display:flex; flex-wrap:wrap; gap:6px 8px; }
.match-team-chip{ display:inline-flex; align-items:center; gap:6px; padding:4px 10px; border-radius:999px; background:#f5f5f5; font-size:13px; font-weight:800; }
.match-dot{ width:10px; height:10px; border-radius:50%; flex-shrink:0; }

.toolbar-reset{ display:flex; gap:8px; padding:8px 12px; border-top:1px dashed #eee; border-bottom:1px dashed #eee; background:#fff; }
.toolbar-reset button{ padding:10px 12px; border-radius:10px; border:1px solid rgba(0,0,0,.12); background:#fff; font-weight:800; }
.toolbar-reset .danger{ border-color:#c21; color:#c21; background:#fef2f2; }

.content { overflow: auto; padding: 12px; }

.panel { background: #fff; border-radius: 14px; box-shadow: 0 4px 12px rgba(0,0,0,.06); padding: 12px; margin-bottom: 12px; }
.panel-title { margin: 0 0 8px 0; font-size: 16px; font-weight: 900; letter-spacing: .06em; }

.board { width: 100%; aspect-ratio: 1 / 1; border: 1px solid #eee; border-radius: 12px; padding: 10px; background: #fafafa; }
.grid.small { width: 100%; height: 100%; position: relative; background: #fafafa;
  background-image: linear-gradient(to right, rgba(0,0,0,.06) 1px, transparent 1px), linear-gradient(to bottom, rgba(0,0,0,.06) 1px, transparent 1px);
  background-size: calc(100%/4) calc(100%/4); border-radius: 12px; overflow: hidden; }
.grid.small .cell { position:absolute; display:grid; place-items:center; background:transparent !important; z-index:5; }
.grid.small .cell-lock-ring{
  position:absolute; z-index:9; pointer-events:none; box-sizing:border-box;
  top: var(--cell-lock-ring-inset-top); right: var(--cell-lock-ring-inset-right);
  bottom: var(--cell-lock-ring-inset-bottom); left: var(--cell-lock-ring-inset-left);
  border: var(--cell-lock-ring-border-width) solid var(--cell-lock-ring-color);
  border-radius: 10px;
}
.grid.small .cell-both-locked::after{ content:''; position:absolute; inset:0; background:rgba(0,0,0,.06); border-radius:12px; pointer-events:none; }
.grid.small .cell-selected { background: rgba(59, 130, 246, 0.15) !important; border: 2px solid #3b82f6 !important; }
.grid.small .cell-disabled { opacity: 0.5; }
.grid.small .cell-num{ position:absolute; left:6%; top:4%; font-size: clamp(10px, 2.8vw, 14px); color:#888; font-weight:800; user-select:none; pointer-events:none; }
.grid.small .badges{ position:relative; display:flex; flex-direction:column; align-items:center; justify-content:center; gap: var(--badge-gap); margin: 0 auto; text-align:center; }
.grid.small .badge { position:relative; width: var(--badge-size); aspect-ratio:1/1; display:flex; align-items:center; justify-content:center; border-radius: var(--badge-radius); border:0 !important; box-shadow:none !important; outline:none !important; background:transparent !important; margin:0 auto; }
.grid.small .badge img{ max-width:100%; max-height:100%; object-fit:contain; display:block; }
.grid.small .badge img.badge-image-locked{
  outline: 4px solid #f01616 !important;
  outline-offset: 3px;
  border-radius:12%;
  box-sizing:border-box;
}
@keyframes fading { 0% {opacity:.85} 50% {opacity:.55} 100% {opacity:.85} }
.grid.small .badge-fade { animation: fading 1.1s infinite; }

.line { position:absolute; height:0; transform-origin:0 0; pointer-events:none; mix-blend-mode:normal; z-index:2; }
.line .stroke-border{ position:absolute; left:0; top:-3.0%; height:6.0%; width:100%; background:#fff; border-radius:999px; }
.line .stroke-color{ position:absolute; left:0; top:-2.2%; height:4.4%; width:100%; background:var(--clr,#fff); border-radius:999px; opacity:1; }
.line.pending .stroke-border{ animation: dashA 0.65s linear infinite; }
.line.pending .stroke-color{ animation: thickenA 0.65s ease-in-out infinite alternate, dashA 0.65s linear infinite; }
@keyframes dashA { 0%{opacity:.60} 50%{opacity:1} 100%{opacity:.60} }
@keyframes thickenA { from{height:4.2%} to{height:4.9%} }

.chips { display: flex; gap: 8px; overflow-x: auto; padding-bottom: 2px; }
.chip { flex: 0 0 auto; display: inline-flex; align-items: center; gap: 8px; padding: 10px 12px; border-radius: 999px; border: 1px solid rgba(0,0,0,.12); background: #fff; font-weight: 900; }
.chip .dot { width: 12px; height: 12px; border-radius: 50%; border: 1px solid rgba(0,0,0,.25); }
.chip.active { background: var(--clr); color: #fff; border-color: var(--clr); }
.chip.active .dot { border-color: rgba(255,255,255,.9); }

.keypad { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; }
.num { height: 54px; border-radius: 12px; border: 1px solid rgba(0,0,0,.10); background: #fff; font-size: 18px; font-weight: 900; letter-spacing: .06em; }
.num:active { transform: translateY(1px); }
.num.active { border-color: #333; box-shadow: 0 0 0 2px #333 inset; }

.warn { color:#d00; font-weight: 900; margin: 4px 2px 10px; }
.collapse { width: 100%; text-align: left; padding: 10px 12px; border-radius: 10px; border: 1px solid rgba(0,0,0,.10); background: #fafafa; font-weight: 800; }
.table-wrap { margin-top: 10px; border: 1px solid #eee; border-radius: 10px; overflow: auto; max-height: 45vh; }
table { width: 100%; border-collapse: collapse; background: #fff; }
th, td { padding: 10px 12px; border-bottom: 1px solid #f0f0f0; font-size: 14px; white-space: nowrap; }
.link { background: none; border: none; color: #0a66c2; font-weight: 800; }
.link.danger { color: #c21; }

.submitbar { position: fixed; left: 0; right: 0; bottom: 0; padding: 8px 12px calc(8px + var(--safe-bottom)); background: rgba(255,255,255,.98); border-top: 1px solid rgba(0,0,0,.06); display: grid; grid-template-columns: 1fr auto; gap: 10px; align-items: center; z-index: 20; }
.summary { display: flex; gap: 14px; font-size: 14px; }
.summary b { font-weight: 900; }
.submit { min-width: 120px; height: 44px; padding: 0 16px; border-radius: 12px; border: none; background: #111; color: #fff; font-size: 16px; font-weight: 900; letter-spacing: .08em; }
.submit:disabled { background: #bbb; }

.team-selector-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.5); display: flex; align-items: center; justify-content: center; z-index: 100; }
.team-selector-panel { background: #fff; border-radius: 16px; padding: 20px; max-width: 90vw; width: 400px; box-shadow: 0 10px 40px rgba(0,0,0,.3); }
.team-selector-title { font-size: 18px; font-weight: 900; margin-bottom: 8px; text-align: center; }
.team-selector-hint { font-size: 12px; font-weight: 700; color: #555; line-height: 1.4; margin-bottom: 12px; text-align: center; }
.team-selector-chips { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 16px; }
.team-selector-chip { display: inline-flex; align-items: center; gap: 8px; padding: 10px 12px; border-radius: 999px; border: 1px solid rgba(0,0,0,.12); background: #fff; font-weight: 900; cursor: pointer; transition: all 0.2s; }
.team-selector-chip:hover:not(:disabled) { background: var(--clr); color: #fff; border-color: var(--clr); }
.team-selector-chip:disabled { cursor: not-allowed; }
.team-selector-chip .dot { width: 12px; height: 12px; border-radius: 50%; border: 1px solid rgba(0,0,0,.25); }
.team-selector-chip:hover:not(:disabled) .dot { border-color: rgba(255,255,255,.9); }
.team-selector-cancel { width: 100%; padding: 10px; border-radius: 10px; border: 1px solid rgba(0,0,0,.12); background: #f5f5f5; font-weight: 800; cursor: pointer; }
.team-selector-cancel:hover { background: #e5e5e5; }
.team-selector-chip.disabled-part { opacity: 0.4; cursor: not-allowed; }
.team-selector-chip.disabled-part:hover:not(:disabled) { background: #fff; color: inherit; border-color: rgba(0,0,0,.12); }

.game-ended-banner{ display:flex; justify-content:center; margin:4px 0; }
.game-ended-text{ padding:6px 12px; background:#fee; color:#c21; border:1px solid #fcc; border-radius:8px; font-weight:900; font-size:14px; }
.score-board.score-board-row{
  display:flex; flex-direction:row; flex-wrap:nowrap; align-items:stretch;
  gap:8px; overflow-x:auto; padding-bottom:4px; -webkit-overflow-scrolling:touch;
}
.score-chip-inline{
  flex:0 0 auto; display:inline-flex; align-items:center; gap:6px;
  padding:8px 10px; background:#fafafa; border:1px solid #eee; border-radius:999px;
  max-width:min(200px, 42vw); min-width:0;
}
.score-chip-inline.top-score{ background:#fff9e6; border-color:#f6c700; }
.sc-rank{ font-weight:900; font-size:12px; color:#888; flex-shrink:0; }
.sc-dot{ width:10px; height:10px; border-radius:50%; border:1px solid rgba(0,0,0,.12); flex-shrink:0; }
.sc-name{ font-weight:800; font-size:13px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; min-width:0; }
.sc-val{ font-weight:900; font-size:14px; color:#111; font-variant-numeric:tabular-nums; flex-shrink:0; margin-left:2px; }
.score-empty{ padding:12px; text-align:center; color:#888; font-weight:800; flex:1 0 100%; }

@media (min-width: 768px) {
  .content { max-width: 820px; margin: 0 auto; }
  .board { padding: 14px; }
  .num { height: 64px; font-size: 20px; }
}
`;
